import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Prices a household against the server's premium engine.
 *
 * All the arithmetic lives on the server (see backend/routes/calculator.py) —
 * this hook only ships the coverage *choices* up and hands the numbers back.
 * Two details matter for how it feels to use:
 *
 * - **Debounced.** Every field change is a round-trip, so edits are coalesced
 *   into one request instead of firing per keystroke/selection.
 * - **Sticky.** The previous quote stays on screen while a new one is in
 *   flight, so the totals never blink to zero mid-edit. Only an error clears
 *   them.
 *
 * Responses are sequenced: a slow earlier request can't overwrite a newer
 * result.
 */
export default function useCalculatorQuote(members, { delay = 250 } = {}) {
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const seqRef = useRef(0);

    // Serialising the payload keeps the effect from re-firing on every render
    // just because `members` is a fresh array identity.
    const payload = JSON.stringify({ members });

    useEffect(() => {
        const parsed = JSON.parse(payload);
        if (!parsed.members.length) {
            setQuote(null);
            return undefined;
        }
        const seq = ++seqRef.current;
        setLoading(true);
        const timer = setTimeout(() => {
            api.post("/member/calculator/quote", parsed)
                .then((r) => {
                    if (seq !== seqRef.current) return; // superseded
                    setQuote(r.data);
                    setError(null);
                })
                .catch((e) => {
                    if (seq !== seqRef.current) return;
                    setError(e);
                    setQuote(null);
                })
                .finally(() => {
                    if (seq === seqRef.current) setLoading(false);
                });
        }, delay);
        return () => clearTimeout(timer);
    }, [payload, delay]);

    return { quote, loading, error };
}
